import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Mail, RefreshCw, Send, Eye, EyeOff, UserPlus, Download, Trash2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["nieuw", "in-behandeling", "afgerond"];

const REPLY_TEMPLATES = [
    {
        id: "ontvangen",
        label: "Melding ontvangen",
        text: "Bedankt voor je melding, {naam}. We hebben hem goed ontvangen en gaan er direct mee aan de slag. Je hoort van ons zodra het is opgelost.\n\nMet vriendelijke groet,\nEd — Club-Fin.nl",
    },
    {
        id: "opgelost",
        label: "Probleem opgelost",
        text: "Goed nieuws, {naam}: het probleem is verholpen. Vernieuw de pagina en probeer het gerust opnieuw. Lukt het alsnog niet? Laat het ons gerust weten — dan duiken we er dieper in.\n\nMet vriendelijke groet,\nEd — Club-Fin.nl",
    },
    {
        id: "meer-info",
        label: "Meer info nodig",
        text: "Bedankt voor je melding, {naam}. Om je goed te kunnen helpen hebben we iets meer informatie nodig. Kun je ons vertellen welke browser je gebruikt en welke stappen je precies deed toen het misging?\n\nMet vriendelijke groet,\nEd — Club-Fin.nl",
    },
];

const fmtDate = (iso) => {
    try {
        return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" });
    } catch {
        return iso;
    }
};

const StatusPill = ({ status }) => (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full ${
        status === "afgerond" || status === "opgelost" || status === "afgehandeld" || status === "goedgekeurd" ? "bg-green-100 text-green-700"
        : status === "in-behandeling" ? "bg-[#0047FF]/10 text-[#0047FF]"
        : "bg-[#FF6B00]/10 text-[#FF6B00]"
    }`}>
        {status || "nieuw"}
    </span>
);

const Admin = () => {
    const [token, setToken] = useState(() => localStorage.getItem("cf_admin_token") || "");
    const [tab, setTab] = useState("proef");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loginError, setLoginError] = useState("");
    const [loading, setLoading] = useState(false);
    const [leads, setLeads] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [visits, setVisits] = useState([]);
    const [trialClicks, setTrialClicks] = useState([]);
    const [pageviews, setPageviews] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [posts, setPosts] = useState([]);
    const [backups, setBackups] = useState([]);
    const [replyFor, setReplyFor] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [replySending, setReplySending] = useState(false);
    const [step, setStep] = useState("password");
    const [pendingToken, setPendingToken] = useState("");
    const [tfaMethods, setTfaMethods] = useState([]);
    const [tfaMethod, setTfaMethod] = useState(null);
    const [tfaCode, setTfaCode] = useState("");
    const [tfaEmailSent, setTfaEmailSent] = useState(false);
    const [tfa, setTfa] = useState({ totp_enabled: false, email_2fa_enabled: false });
    const [totpSetup, setTotpSetup] = useState(null);
    const [totpEnableCode, setTotpEnableCode] = useState("");
    const [emailEnabling, setEmailEnabling] = useState(false);
    const [emailEnableCode, setEmailEnableCode] = useState("");
    const [range, setRange] = useState({ from: "", to: "" });
    const importFileRef = useRef(null);
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);

    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const load = async () => {
        try {
            const [l, t, s, v, c, tc, pv, tf, pp, bu] = await Promise.all([
                axios.get(`${API}/admin/leads`, auth),
                axios.get(`${API}/admin/tickets`, auth),
                axios.get(`${API}/admin/subscribers`, auth),
                axios.get(`${API}/admin/campaign-visits`, auth),
                axios.get(`${API}/admin/campaigns`, auth),
                axios.get(`${API}/admin/trial-clicks`, auth),
                axios.get(`${API}/admin/pageviews`, auth),
                axios.get(`${API}/auth/2fa/status`, auth),
                axios.get(`${API}/admin/posts`, auth),
                axios.get(`${API}/admin/subscriber-backups`, auth),
            ]);
            setLeads(l.data); setTickets(t.data); setSubscribers(s.data); setVisits(v.data); setCampaigns(c.data); setTrialClicks(tc.data); setPageviews(pv.data); setTfa(tf.data); setPosts(pp.data); setBackups(bu.data);
        } catch (e) {
            if (e.response?.status === 401) {
                localStorage.removeItem("cf_admin_token");
                setToken("");
            } else {
                toast.error("Gegevens laden mislukt.");
            }
        }
    };

    useEffect(() => { if (token) load(); }, [token]);

    const login = async (e) => {
        e.preventDefault();
        setLoading(true);
        setLoginError("");
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            if (data.requires_2fa) {
                setPendingToken(data.pending_token);
                setTfaMethods(data.methods);
                setStep("2fa");
                if (data.methods.length === 1) chooseTfaMethod(data.methods[0], data.pending_token);
                return;
            }
            localStorage.setItem("cf_admin_token", data.token);
            setToken(data.token);
        } catch (err) {
            const d = err.response?.data?.detail;
            setLoginError(typeof d === "string" ? d : "Inloggen mislukt.");
        } finally {
            setLoading(false);
        }
    };

    const chooseTfaMethod = async (m, pt = pendingToken) => {
        setTfaMethod(m);
        setTfaCode("");
        setLoginError("");
        if (m === "email") {
            setTfaEmailSent(false);
            try {
                await axios.post(`${API}/auth/2fa/email/send`, { pending_token: pt });
                setTfaEmailSent(true);
            } catch (err) {
                const d = err.response?.data?.detail;
                setLoginError(typeof d === "string" ? d : "Code versturen mislukt.");
            }
        }
    };

    const verify2fa = async (e) => {
        e.preventDefault();
        setLoading(true);
        setLoginError("");
        try {
            const { data } = await axios.post(`${API}/auth/login/2fa`, { pending_token: pendingToken, method: tfaMethod, code: tfaCode.trim() });
            localStorage.setItem("cf_admin_token", data.token);
            setToken(data.token);
            setStep("password"); setPendingToken(""); setTfaMethods([]); setTfaMethod(null); setTfaCode(""); setTfaEmailSent(false); setPassword("");
        } catch (err) {
            const d = err.response?.data?.detail;
            setLoginError(typeof d === "string" ? d : "Verificatie mislukt.");
        } finally {
            setLoading(false);
        }
    };

    const refreshTfa = async () => {
        try {
            const { data } = await axios.get(`${API}/auth/2fa/status`, auth);
            setTfa(data);
        } catch { /* status blijft staan */ }
    };

    const totpStart = async () => {
        try {
            const { data } = await axios.post(`${API}/auth/2fa/totp/setup`, {}, auth);
            setTotpSetup(data); setTotpEnableCode("");
        } catch {
            toast.error("Koppeling starten mislukt");
        }
    };

    const totpEnable = async () => {
        try {
            await axios.post(`${API}/auth/2fa/totp/enable`, { code: totpEnableCode.trim() }, auth);
            toast.success("Authenticator-app gekoppeld");
            setTotpSetup(null);
            refreshTfa();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Activeren mislukt");
        }
    };

    const totpDisable = async () => {
        try {
            await axios.post(`${API}/auth/2fa/totp/disable`, {}, auth);
            toast.success("Authenticator-app uitgeschakeld");
            setTotpSetup(null);
            refreshTfa();
        } catch {
            toast.error("Uitschakelen mislukt");
        }
    };

    const emailStart = async () => {
        setEmailEnabling(true);
        try {
            await axios.post(`${API}/auth/2fa/email/enable/start`, {}, auth);
            toast.success("Code verstuurd naar je e-mailadres");
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Versturen mislukt");
            setEmailEnabling(false);
        }
    };

    const emailConfirm = async () => {
        try {
            await axios.post(`${API}/auth/2fa/email/enable/confirm`, { code: emailEnableCode.trim() }, auth);
            toast.success("E-mailverificatie ingeschakeld");
            setEmailEnabling(false); setEmailEnableCode("");
            refreshTfa();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Bevestigen mislukt");
        }
    };

    const emailDisable = async () => {
        try {
            await axios.post(`${API}/auth/2fa/email/disable`, {}, auth);
            toast.success("E-mailverificatie uitgeschakeld");
            refreshTfa();
        } catch {
            toast.error("Uitschakelen mislukt");
        }
    };

    const setStatus = async (kind, id, status) => {
        try {
            await axios.patch(`${API}/admin/${kind}/${id}`, { status }, auth);
            toast.success("Status bijgewerkt");
            load();
        } catch {
            toast.error("Bijwerken mislukt");
        }
    };

    const deleteLead = async (lead) => {
        if (!window.confirm(`Aanvraag van ${lead.naam} (${lead.email}) definitief verwijderen?`)) return;
        try {
            await axios.delete(`${API}/admin/leads/${lead.id}`, auth);
            toast.success("Aanvraag verwijderd");
            load();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Verwijderen mislukt");
        }
    };

    const approvePost = async (post) => {
        try {
            await axios.patch(`${API}/admin/posts/${post.id}`, { status: "goedgekeurd" }, auth);
            toast.success("Bevinding goedgekeurd — staat nu op de community-pagina");
            load();
        } catch {
            toast.error("Goedkeuren mislukt");
        }
    };

    const deletePost = async (post) => {
        if (!window.confirm(`Bevinding van ${post.naam} definitief verwijderen?`)) return;
        try {
            await axios.delete(`${API}/admin/posts/${post.id}`, auth);
            toast.success("Bevinding verwijderd");
            load();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Verwijderen mislukt");
        }
    };

    const restoreBackup = async (backup) => {
        if (!window.confirm(`De huidige ledenlijst (${subscribers.length}) vervangen door de backup van ${fmtDate(backup.created_at)} (${backup.count} leden)? Van de huidige lijst wordt eerst weer een backup gemaakt.`)) return;
        try {
            const { data } = await axios.post(`${API}/admin/subscriber-backups/${backup.id}/restore`, { confirm_replace: true }, auth);
            toast.success(`Backup hersteld: ${data.count} leden teruggezet`);
            load();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Herstellen mislukt");
        }
    };

    const sendReply = async (ticketId) => {
        setReplySending(true);
        try {
            await axios.post(`${API}/admin/tickets/${ticketId}/reply`, { message: replyText }, auth);
            toast.success("Antwoord per e-mail verzonden");
            setReplyFor(null); setReplyText("");
            load();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Verzenden mislukt");
        } finally {
            setReplySending(false);
        }
    };

    const sendCampaign = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const { data } = await axios.post(`${API}/admin/campaigns/send`, { subject, message }, auth);
            toast.success(`Verzonden naar ${data.sent} van ${data.total} abonnees`);
            setSubject(""); setMessage("");
            load();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Verzenden mislukt");
        } finally {
            setSending(false);
        }
    };

    const logout = () => {
        localStorage.removeItem("cf_admin_token");
        setToken("");
    };

    const inputCls = "w-full rounded-xl bg-white border border-black/15 px-4 py-3 text-sm text-[#0A0A0A] outline-none focus:border-[#0047FF] transition-colors";

    if (!token) {
        return (
            <div data-testid="admin-login" className="min-h-screen bg-[#0A0A0A] noise-overlay flex items-center justify-center px-6">
                {step === "2fa" ? (
                    <form onSubmit={verify2fa} className="w-full max-w-sm rounded-2xl border border-white/15 bg-white/5 backdrop-blur-xl p-8">
                        <a href="/" className="block mb-1" aria-label="MeerjarenplanOnline.nl">
                            <img src="/images/logo.png" alt="MeerjarenplanOnline.nl" className="h-8 w-auto" />
                        </a>
                        <p className="text-white/50 text-xs tracking-[0.2em] uppercase font-bold mb-8">Tweestapsverificatie</p>
                        {tfaMethods.length > 1 && (
                            <div className="flex gap-2 mb-4">
                                <button
                                    data-testid="admin-2fa-method-totp"
                                    type="button"
                                    onClick={() => chooseTfaMethod("totp")}
                                    className={`flex-1 rounded-full text-xs font-bold px-4 py-2.5 transition-colors ${tfaMethod === "totp" ? "bg-[#FF6B00] text-white" : "border border-white/20 text-white/60 hover:text-white"}`}
                                >
                                    Authenticator-app
                                </button>
                                <button
                                    data-testid="admin-2fa-method-email"
                                    type="button"
                                    onClick={() => chooseTfaMethod("email")}
                                    className={`flex-1 rounded-full text-xs font-bold px-4 py-2.5 transition-colors ${tfaMethod === "email" ? "bg-[#FF6B00] text-white" : "border border-white/20 text-white/60 hover:text-white"}`}
                                >
                                    Code per e-mail
                                </button>
                            </div>
                        )}
                        {tfaMethod === "email" && (
                            <p data-testid="admin-2fa-email-note" className="text-white/50 text-xs mb-3">
                                {tfaEmailSent ? "We hebben een code gestuurd naar je e-mailadres." : "De code wordt verstuurd..."}
                            </p>
                        )}
                        {tfaMethod === "totp" && (
                            <p className="text-white/50 text-xs mb-3">Open je authenticator-app en voer de 6-cijferige code in.</p>
                        )}
                        {tfaMethod && (
                            <input
                                data-testid="admin-2fa-code"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                value={tfaCode}
                                onChange={(e) => setTfaCode(e.target.value)}
                                placeholder="6-cijferige code"
                                className="w-full rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-white placeholder-white/40 text-sm text-center tracking-[0.4em] outline-none focus:border-white/60 mb-3"
                            />
                        )}
                        {loginError && <p data-testid="admin-login-error" className="text-[#FF6B00] text-sm font-bold mb-3">{loginError}</p>}
                        <button data-testid="admin-2fa-submit" type="submit" disabled={loading || !tfaMethod || tfaCode.trim().length < 6} className="w-full rounded-full bg-[#FF6B00] hover:bg-[#e05e00] text-white font-bold text-sm px-6 py-3.5 transition-colors disabled:opacity-60">
                            {loading ? "Bezig..." : "Verifiëren"}
                        </button>
                        <div className="mt-4 flex items-center justify-between text-xs">
                            <button
                                data-testid="admin-2fa-back"
                                type="button"
                                onClick={() => { setStep("password"); setPendingToken(""); setTfaMethods([]); setTfaMethod(null); setTfaCode(""); setLoginError(""); }}
                                className="text-white/40 hover:text-white transition-colors"
                            >
                                Terug naar inloggen
                            </button>
                            {tfaMethod === "email" && (
                                <button data-testid="admin-2fa-resend" type="button" onClick={() => chooseTfaMethod("email")} className="text-white/40 hover:text-white transition-colors">
                                    Opnieuw versturen
                                </button>
                            )}
                        </div>
                    </form>
                ) : (
                <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-white/15 bg-white/5 backdrop-blur-xl p-8">
                    <a href="/" className="block mb-1" aria-label="MeerjarenplanOnline.nl">
                        <img src="/images/logo.png" alt="MeerjarenplanOnline.nl" className="h-8 w-auto" />
                    </a>
                    <p className="text-white/50 text-xs tracking-[0.2em] uppercase font-bold mb-8">Beheerdersomgeving</p>
                    <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mailadres" className="w-full rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-white placeholder-white/40 text-sm outline-none focus:border-white/60 mb-3" />
                    <div className="relative mb-3">
                        <input data-testid="admin-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Wachtwoord" className="w-full rounded-xl bg-white/10 border border-white/20 px-4 py-3 pr-12 text-white placeholder-white/40 text-sm outline-none focus:border-white/60" />
                        <button
                            data-testid="admin-password-toggle"
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors p-1"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    {loginError && <p data-testid="admin-login-error" className="text-[#FF6B00] text-sm font-bold mb-3">{loginError}</p>}
                    <button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full rounded-full bg-[#FF6B00] hover:bg-[#e05e00] text-white font-bold text-sm px-6 py-3.5 transition-colors disabled:opacity-60">
                        {loading ? "Bezig..." : "Inloggen"}
                    </button>
                </form>
                )}
            </div>
        );
    }

    const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    const inRange = (iso) => {
        const t = new Date(iso).getTime();
        if (range.from && t < new Date(`${range.from}T00:00:00`).getTime()) return false;
        if (range.to && t > new Date(`${range.to}T23:59:59.999`).getTime()) return false;
        return true;
    };

    const setPreset = (preset) => {
        const now = new Date();
        const monday = new Date(now);
        monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
        if (preset === "week") setRange({ from: toISODate(monday), to: "" });
        if (preset === "prevweek") {
            const start = new Date(monday); start.setDate(start.getDate() - 7);
            const end = new Date(monday); end.setDate(end.getDate() - 1);
            setRange({ from: toISODate(start), to: toISODate(end) });
        }
        if (preset === "month") setRange({ from: toISODate(new Date(now.getFullYear(), now.getMonth(), 1)), to: "" });
        if (preset === "all") setRange({ from: "", to: "" });
    };

    const rangeLabel = () => {
        if (!range.from && !range.to) return "Alle metingen";
        const f = range.from ? new Date(`${range.from}T00:00:00`).toLocaleDateString("nl-NL", { dateStyle: "medium" }) : "begin";
        const t = range.to ? new Date(`${range.to}T00:00:00`).toLocaleDateString("nl-NL", { dateStyle: "medium" }) : "heden";
        return `${f} — ${t}`;
    };

    const filteredClicks = trialClicks.filter((c) => inRange(c.created_at));
    const filteredViews = pageviews.filter((p) => inRange(p.created_at));

    const exportSubscribers = () => {
        const rows = [["email", "bron", "aangemeld_op"], ...subscribers.map((s) => [s.email, s.bron || "", s.created_at || ""])];
        const csv = "\uFEFF" + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\r\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `community-leden-${toISODate(new Date())}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const importSubscribers = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                const text = String(reader.result).replace(/^\uFEFF/, "");
                const lines = text.split(/\r?\n/).filter((l) => l.trim());
                if (lines.length < 2) throw new Error("leeg");
                const sep = lines[0].includes(";") ? ";" : ",";
                const parse = (line) => line.split(sep).map((c) => c.trim().replace(/^"|"$/g, "").replace(/""/g, '"'));
                const header = parse(lines[0]).map((h) => h.toLowerCase());
                const ei = header.indexOf("email");
                if (ei === -1) throw new Error("kolom ontbreekt");
                const bi = header.indexOf("bron");
                const di = header.indexOf("aangemeld_op");
                const subs = lines.slice(1).map(parse)
                    .filter((cols) => cols[ei] && cols[ei].includes("@"))
                    .map((cols) => ({
                        email: cols[ei],
                        bron: bi >= 0 && cols[bi] ? cols[bi] : "import",
                        created_at: di >= 0 && cols[di] ? cols[di] : undefined,
                    }));
                if (!subs.length) throw new Error("leeg");
                if (!window.confirm(`De ledenlijst (${subscribers.length}) wordt VOLLEDIG vervangen door ${subs.length} adressen uit "${file.name}". Er wordt eerst automatisch een backup gemaakt. Doorgaan?`)) return;
                const { data } = await axios.put(`${API}/admin/subscribers`, { subscribers: subs, confirm_replace: true }, auth);
                toast.success(`Lijst vervangen: ${data.count} leden erin${data.skipped ? `, ${data.skipped} overgeslagen` : ""} (backup van ${data.backed_up} bewaard)`);
                load();
            } catch (err) {
                const d = err.response?.data?.detail;
                toast.error(typeof d === "string" ? d : "Bestand kon niet worden gelezen. Gebruik het gedownloade CSV-formaat (kolommen: email, bron, aangemeld_op).");
            } finally {
                e.target.value = "";
            }
        };
        reader.readAsText(file);
    };

    const TABS = [
        { id: "proef", label: `Proef (${trialClicks.length})` },
        { id: "leads", label: `Aanvragen (${leads.length})` },
        { id: "tickets", label: `Tickets (${tickets.filter((t) => t.status !== "opgelost" && t.status !== "afgehandeld").length})` },
        { id: "leden", label: `Leden (${subscribers.length})` },
        { id: "berichten", label: `Berichten (${posts.filter((p) => p.status === "nieuw").length})` },
        { id: "campagne", label: "Campagne" },
        { id: "bezoeken", label: `Bezoeken (${visits.length})` },
        { id: "beveiliging", label: "Beveiliging" },
        { id: "handleiding", label: "Handleiding" },
    ];

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#F9FAFB]">
            <header className="bg-[#0A0A0A] border-b border-white/10">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
                    <a href="/" className="font-heading text-white text-xl font-black tracking-tight">
                        Meerjarenplan<span className="text-[#FF6B00]">Online.nl</span> <span className="text-white/40 text-sm font-bold">Statistieken</span>
                    </a>
                    <div className="flex items-center gap-3">
                        <a
                            data-testid="admin-invite-link"
                            href="https://app.meerjarenplanonline.nl/uitnodigingen"
                            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-bold transition-colors"
                            title="Maak uitnodigingscodes voor medewerkers aan in de app"
                        >
                            <UserPlus className="w-4 h-4" /> Team uitnodigen
                        </a>
                        <button data-testid="admin-refresh" onClick={load} className="text-white/60 hover:text-white p-2 transition-colors" aria-label="Vernieuwen">
                            <RefreshCw className="w-4 h-4" />
                        </button>
                        <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-bold transition-colors">
                            <LogOut className="w-4 h-4" /> Uitloggen
                        </button>
                    </div>
                </div>
                <div className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`px-5 py-3 text-sm font-bold whitespace-nowrap border-b-2 transition-colors ${
                                tab === t.id ? "text-white border-[#FF6B00]" : "text-white/40 border-transparent hover:text-white/70"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-10">
                {tab === "handleiding" && (
                    <div data-testid="admin-handleiding" className="max-w-2xl space-y-6">
                        <div className="rounded-2xl bg-white border border-black/10 p-6 space-y-4">
                            <p className="font-heading font-bold text-lg tracking-tight">Mailadressen & routes</p>
                            <div className="space-y-3 text-sm text-[#0A0A0A]/70 leading-relaxed">
                                <p><span className="font-bold text-[#0A0A0A]">info@meerjarenplanonline.nl</span> — het meldpunt van het systeem. Hier komen alle notificaties binnen: nieuwe aanvragen, tickets en prikbord-berichten.</p>
                                <p><span className="font-bold text-[#0A0A0A]">info@club-fin.nl</span> — jouw publieke contactadres. Staat overal op de site en is het antwoordadres van alle systeemberichten; bezoekersmail komt hier binnen.</p>
                                <p><span className="font-bold text-[#0A0A0A]">admin@club-fin.nl</span> — jouw beheerdersaccount voor dit dashboard. Kies bij 2FA bij voorkeur de authenticator-app.</p>
                            </div>
                            <div className="border-t border-black/5 pt-4">
                                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]/40 mb-2">Uitgaande systeemberichten</p>
                                <ul className="text-sm text-[#0A0A0A]/70 space-y-1.5 list-disc pl-5">
                                    <li>Bevestigingscode → de aanmelder (stap 1 van community-aanmelding)</li>
                                    <li>Welkomstmail → het nieuwe lid (ná bevestiging)</li>
                                    <li>Notificaties (aanvraag / ticket / bevinding) → info@meerjarenplanonline.nl</li>
                                    <li>Antwoord op een ticket → de melder (knop bij het ticket)</li>
                                </ul>
                                <p className="mt-3 text-xs text-[#0A0A0A]/40">Verzonden als "MeerjarenplanOnline.nl"; antwoorden lopen automatisch door naar info@club-fin.nl.</p>
                            </div>
                        </div>

                        <div className="rounded-2xl bg-white border border-black/10 p-6 space-y-3">
                            <p className="font-heading font-bold text-lg tracking-tight">Community</p>
                            <ul className="text-sm text-[#0A0A0A]/70 space-y-1.5 list-disc pl-5 leading-relaxed">
                                <li>Aanmelden is tweestaps: e-mailadres → code per mail → pas dan lid. Dubbele adressen worden automatisch tegengehouden.</li>
                                <li>Alleen leden kunnen een bevinding delen; nieuwe berichten wachten in de tab <strong>Berichten</strong> op jouw goedkeuring en verschijnen dan openbaar op de community-pagina.</li>
                                <li>Niet-leden gebruiken de meld-box op de community-pagina; die verschijnt hier als <strong>Ticket</strong>.</li>
                            </ul>
                        </div>

                        <div className="rounded-2xl bg-white border border-black/10 p-6 space-y-3">
                            <p className="font-heading font-bold text-lg tracking-tight">Ledenlijst</p>
                            <ul className="text-sm text-[#0A0A0A]/70 space-y-1.5 list-disc pl-5 leading-relaxed">
                                <li><strong>Download als bestand</strong>: Excel-vriendelijk CSV van alle leden.</li>
                                <li><strong>Upload bestand</strong>: vervangt de hele lijst — er wordt eerst automatisch een backup gemaakt.</li>
                                <li>Onderaan de tab kun je elke eerdere versie met één klik herstellen.</li>
                            </ul>
                        </div>

                        <div className="rounded-2xl bg-white border border-black/10 p-6 space-y-3">
                            <p className="font-heading font-bold text-lg tracking-tight">Statistieken & meten</p>
                            <ul className="text-sm text-[#0A0A0A]/70 space-y-1.5 list-disc pl-5 leading-relaxed">
                                <li>De tab <strong>Proef</strong> telt kliks op "Proef starten" en paginabezoeken; filter met Van/Tot of de snelknoppen.</li>
                                <li>Eigen testklikken herkennen: surf via <span className="font-mono text-xs bg-black/5 rounded px-1.5 py-0.5">meerjarenplanonline.nl/?bron=ed</span> — jouw kliks krijgen dan een eigen label.</li>
                                <li>Hetzelfde trucje werkt voor kanalen, bijv. <span className="font-mono text-xs bg-black/5 rounded px-1.5 py-0.5">?bron=linkedin</span>.</li>
                            </ul>
                        </div>

                        <div className="rounded-2xl bg-white border border-black/10 p-6 space-y-3">
                            <p className="font-heading font-bold text-lg tracking-tight">Beveiliging</p>
                            <ul className="text-sm text-[#0A0A0A]/70 space-y-1.5 list-disc pl-5 leading-relaxed">
                                <li>Zet in de tab <strong>Beveiliging</strong> tweestapsverificatie aan (authenticator-app aanbevolen).</li>
                                <li>Wachtwoord wijzigen: pas ADMIN_PASSWORD aan via Re-publish → Secrets in Emergent, daarna opnieuw publiceren.</li>
                                <li>Uitloggen rechtsboven; sessies lopen na 12 uur vanzelf af.</li>
                            </ul>
                        </div>
                    </div>
                )}

                {tab === "beveiliging" && (
                    <div data-testid="admin-security" className="max-w-2xl">
                        <div className="rounded-2xl bg-white border border-black/10 p-6 space-y-6">
                            <div>
                                <p className="font-heading font-bold text-lg tracking-tight">Tweestapsverificatie (2FA)</p>
                                <p className="mt-1 text-sm text-[#0A0A0A]/50 leading-relaxed">
                                    Extra beveiliging bij het inloggen: na je wachtwoord voer je een eenmalige code in.
                                    Schakel één of beide methoden in; bij het inloggen kies je welke je gebruikt.
                                </p>
                            </div>

                            <div className="border-t border-black/5 pt-5 space-y-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <p className="font-bold text-sm">Authenticator-app</p>
                                        <p className="text-xs text-[#0A0A0A]/50">Bijv. Google Authenticator of Microsoft Authenticator</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span data-testid="totp-status" className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full ${tfa.totp_enabled ? "bg-green-100 text-green-700" : "bg-black/5 text-[#0A0A0A]/40"}`}>
                                            {tfa.totp_enabled ? "Aan" : "Uit"}
                                        </span>
                                        {tfa.totp_enabled ? (
                                            <button data-testid="totp-disable" onClick={totpDisable} className="text-sm font-bold rounded-full border border-black/15 text-[#0A0A0A]/60 hover:border-black/40 px-4 py-2 transition-colors">
                                                Uitschakelen
                                            </button>
                                        ) : (
                                            !totpSetup && (
                                                <button data-testid="totp-setup" onClick={totpStart} className="text-sm font-bold rounded-full bg-[#0047FF] hover:bg-[#0036CC] text-white px-4 py-2 transition-colors">
                                                    Instellen
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>
                                {totpSetup && !tfa.totp_enabled && (
                                    <div className="rounded-xl bg-[#F9FAFB] border border-black/10 p-4 space-y-3">
                                        <p className="text-xs text-[#0A0A0A]/60">1. Scan deze QR-code met je authenticator-app (of voer de sleutel handmatig in):</p>
                                        <img data-testid="totp-qr" src={totpSetup.qr} alt="QR-code voor authenticator-app" className="w-40 h-40 rounded-lg border border-black/10 bg-white" />
                                        <p data-testid="totp-secret" className="font-mono text-xs bg-white border border-black/10 rounded-lg px-3 py-2 select-all break-all">{totpSetup.secret}</p>
                                        <p className="text-xs text-[#0A0A0A]/60">2. Voer de 6-cijferige code uit de app in om te activeren:</p>
                                        <div className="flex gap-2">
                                            <input data-testid="totp-enable-code" inputMode="numeric" value={totpEnableCode} onChange={(e) => setTotpEnableCode(e.target.value)} placeholder="123456" className={`${inputCls} max-w-[160px]`} />
                                            <button data-testid="totp-enable-submit" onClick={totpEnable} disabled={totpEnableCode.trim().length < 6} className="text-sm font-bold rounded-full bg-[#0047FF] hover:bg-[#0036CC] text-white px-5 py-2 transition-colors disabled:opacity-50">
                                                Activeer
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="border-t border-black/5 pt-5 space-y-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <p className="font-bold text-sm">Code per e-mail</p>
                                        <p className="text-xs text-[#0A0A0A]/50">Eenmalige code naar admin@club-fin.nl</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span data-testid="email-2fa-status" className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full ${tfa.email_2fa_enabled ? "bg-green-100 text-green-700" : "bg-black/5 text-[#0A0A0A]/40"}`}>
                                            {tfa.email_2fa_enabled ? "Aan" : "Uit"}
                                        </span>
                                        {tfa.email_2fa_enabled ? (
                                            <button data-testid="email-2fa-disable" onClick={emailDisable} className="text-sm font-bold rounded-full border border-black/15 text-[#0A0A0A]/60 hover:border-black/40 px-4 py-2 transition-colors">
                                                Uitschakelen
                                            </button>
                                        ) : (
                                            !emailEnabling && (
                                                <button data-testid="email-2fa-start" onClick={emailStart} className="text-sm font-bold rounded-full bg-[#0047FF] hover:bg-[#0036CC] text-white px-4 py-2 transition-colors">
                                                    Zet aan
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>
                                {emailEnabling && !tfa.email_2fa_enabled && (
                                    <div className="rounded-xl bg-[#F9FAFB] border border-black/10 p-4 space-y-3">
                                        <p className="text-xs text-[#0A0A0A]/60">Er is een code verstuurd naar admin@club-fin.nl. Voer die hier in om te bevestigen dat de mail aankomt:</p>
                                        <div className="flex gap-2">
                                            <input data-testid="email-2fa-code" inputMode="numeric" value={emailEnableCode} onChange={(e) => setEmailEnableCode(e.target.value)} placeholder="123456" className={`${inputCls} max-w-[160px]`} />
                                            <button data-testid="email-2fa-confirm" onClick={emailConfirm} disabled={emailEnableCode.trim().length < 6} className="text-sm font-bold rounded-full bg-[#0047FF] hover:bg-[#0036CC] text-white px-5 py-2 transition-colors disabled:opacity-50">
                                                Bevestig
                                            </button>
                                            <button data-testid="email-2fa-cancel" onClick={() => { setEmailEnabling(false); setEmailEnableCode(""); }} className="text-sm font-bold text-[#0A0A0A]/50 hover:text-[#0A0A0A] transition-colors px-2">
                                                Annuleer
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {tab === "proef" && (
                    <div data-testid="admin-trial-clicks" className="space-y-6">
                        <div className="rounded-2xl bg-white border border-black/10 p-5 flex flex-wrap items-end gap-3">
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/40 mb-1">Van</label>
                                <input
                                    data-testid="proef-filter-from"
                                    type="date"
                                    value={range.from}
                                    onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                                    className="text-sm border border-black/15 rounded-lg px-3 py-2 bg-white"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/40 mb-1">Tot</label>
                                <input
                                    data-testid="proef-filter-to"
                                    type="date"
                                    value={range.to}
                                    onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                                    className="text-sm border border-black/15 rounded-lg px-3 py-2 bg-white"
                                />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button data-testid="proef-preset-week" onClick={() => setPreset("week")} className="text-xs font-bold rounded-full border border-[#0047FF]/30 text-[#0047FF] hover:bg-[#0047FF]/5 px-4 py-2 transition-colors">Deze week</button>
                                <button data-testid="proef-preset-prevweek" onClick={() => setPreset("prevweek")} className="text-xs font-bold rounded-full border border-[#0047FF]/30 text-[#0047FF] hover:bg-[#0047FF]/5 px-4 py-2 transition-colors">Vorige week</button>
                                <button data-testid="proef-preset-month" onClick={() => setPreset("month")} className="text-xs font-bold rounded-full border border-[#0047FF]/30 text-[#0047FF] hover:bg-[#0047FF]/5 px-4 py-2 transition-colors">Deze maand</button>
                                <button data-testid="proef-preset-all" onClick={() => setPreset("all")} className="text-xs font-bold rounded-full border border-black/15 text-[#0A0A0A]/50 hover:text-[#0A0A0A] px-4 py-2 transition-colors">Alles</button>
                            </div>
                            <p data-testid="proef-range-label" className="ml-auto text-xs font-bold text-[#0A0A0A]/50">{rangeLabel()}</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="rounded-2xl bg-white border border-black/10 p-6">
                                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0047FF]">Kliks</p>
                                <p data-testid="proef-clicks-count" className="mt-2 font-heading font-black text-4xl tracking-tight">{filteredClicks.length}</p>
                                <p className="mt-1 text-xs text-[#0A0A0A]/40">op "Proef starten" in deze periode</p>
                            </div>
                            <div className="rounded-2xl bg-white border border-black/10 p-6">
                                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]/60">Paginabezoeken</p>
                                <p data-testid="proef-views-count" className="mt-2 font-heading font-black text-4xl tracking-tight">{filteredViews.length}</p>
                                <p className="mt-1 text-xs text-[#0A0A0A]/40">unieke sessies in deze periode</p>
                            </div>
                            <div className="rounded-2xl bg-white border border-black/10 p-6">
                                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B00]">Doorklik-ratio</p>
                                <p data-testid="proef-ratio" className="mt-2 font-heading font-black text-4xl tracking-tight">
                                    {filteredViews.length ? `${Math.round((filteredClicks.length / filteredViews.length) * 100)}%` : "—"}
                                </p>
                                <p className="mt-1 text-xs text-[#0A0A0A]/40">van bezoekers klikt op "Proef starten"</p>
                            </div>
                        </div>

                        <div className="rounded-2xl bg-white border border-black/10 overflow-hidden">
                            {filteredClicks.length === 0 && <p className="p-6 text-[#0A0A0A]/50">Geen kliks op "Proef starten" in deze periode.</p>}
                            {filteredClicks.map((c) => (
                                <div key={c.id} className="flex items-center gap-4 px-6 py-4 border-b border-black/5 last:border-b-0 text-sm">
                                    <span className="font-bold text-[#0047FF]">Proef starten</span>
                                    <span className="text-[#0A0A0A]/50 text-xs">{c.source}</span>
                                    <span className="ml-auto text-xs text-[#0A0A0A]/40">{fmtDate(c.created_at)}</span>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-[#0A0A0A]/40">Let op: dit telt kliks op de landingspagina. Echte proefregistraties (met e-mailadres) staan in het app-project.</p>
                    </div>
                )}

                {tab === "leads" && (
                    <div data-testid="admin-leads" className="space-y-4">
                        {leads.length === 0 && <p className="text-[#0A0A0A]/50">Nog geen aanvragen.</p>}
                        {leads.map((l) => (
                            <div key={l.id} className="rounded-2xl bg-white border border-black/10 p-6 flex flex-wrap gap-4 items-start justify-between">
                                <div className="min-w-0">
                                    <p className="font-heading font-bold text-lg tracking-tight">{l.naam} <span className="text-[#0A0A0A]/40 text-sm font-medium">— {l.bedrijf || "geen organisatie"}</span></p>
                                    <p className="text-sm text-[#0A0A0A]/60">{l.email} · {fmtDate(l.created_at)}</p>
                                    {l.bericht && <p className="mt-2 text-sm text-[#0A0A0A]/70 bg-[#F9FAFB] rounded-lg p-3">{l.bericht}</p>}
                                </div>
                                <div className="flex items-center gap-3">
                                    <StatusPill status={l.status} />
                                    <select
                                        data-testid={`lead-status-${l.id}`}
                                        value={l.status || "nieuw"}
                                        onChange={(e) => setStatus("leads", l.id, e.target.value)}
                                        className="text-sm border border-black/15 rounded-lg px-3 py-2 bg-white"
                                    >
                                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    <button
                                        data-testid={`lead-delete-${l.id}`}
                                        onClick={() => deleteLead(l)}
                                        className="text-[#0A0A0A]/30 hover:text-red-600 p-2 transition-colors"
                                        aria-label={`Aanvraag van ${l.naam} verwijderen`}
                                        title="Verwijderen"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "tickets" && (
                    <div data-testid="admin-tickets" className="space-y-4">
                        {tickets.length === 0 && <p className="text-[#0A0A0A]/50">Nog geen tickets.</p>}
                        {tickets.map((t) => (
                            <div key={t.id} className="rounded-2xl bg-white border border-black/10 p-6">
                                <div className="flex flex-wrap gap-4 items-start justify-between">
                                    <div className="min-w-0">
                                        <p className="font-heading font-bold text-lg tracking-tight">{t.onderwerp}</p>
                                        <p className="text-sm text-[#0A0A0A]/60">{t.naam} · {t.email} · {fmtDate(t.created_at)}</p>
                                        <p className="mt-2 text-sm text-[#0A0A0A]/70 bg-[#F9FAFB] rounded-lg p-3">{t.bericht}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <StatusPill status={t.status || "open"} />
                                        <button
                                            data-testid={`ticket-toggle-${t.id}`}
                                            onClick={() => setStatus("tickets", t.id, (t.status || "open") === "afgehandeld" ? "open" : "afgehandeld")}
                                            className={`text-sm font-bold rounded-lg px-3 py-2 transition-colors border ${
                                                (t.status || "open") === "afgehandeld"
                                                    ? "border-black/15 text-[#0A0A0A]/50 hover:text-[#0A0A0A] hover:border-black/40"
                                                    : "border-green-600/30 text-green-700 hover:bg-green-50"
                                            }`}
                                        >
                                            {(t.status || "open") === "afgehandeld" ? "Heropen" : "Markeer afgehandeld"}
                                        </button>
                                    </div>
                                </div>
                                {t.replies?.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        {t.replies.map((r, i) => (
                                            <p key={i} className="text-xs text-[#0A0A0A]/50 bg-[#0047FF]/5 border-l-2 border-[#0047FF] rounded-r-lg px-3 py-2">
                                                <span className="font-bold text-[#0047FF]">Beantwoord {fmtDate(r.sent_at)}:</span> {r.message}
                                            </p>
                                        ))}
                                    </div>
                                )}
                                {replyFor === t.id ? (
                                    <div className="mt-4 space-y-3">
                                        <div className="flex flex-wrap gap-2">
                                            {REPLY_TEMPLATES.map((tpl) => (
                                                <button
                                                    key={tpl.id}
                                                    data-testid={`ticket-reply-template-${tpl.id}-${t.id}`}
                                                    type="button"
                                                    onClick={() => setReplyText(tpl.text.replace("{naam}", t.naam))}
                                                    className="text-xs font-bold rounded-full border border-[#0047FF]/30 text-[#0047FF] hover:bg-[#0047FF]/5 px-3.5 py-1.5 transition-colors"
                                                >
                                                    {tpl.label}
                                                </button>
                                            ))}
                                        </div>
                                        <textarea
                                            data-testid={`ticket-reply-text-${t.id}`}
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value)}
                                            placeholder={`Je antwoord aan ${t.naam} (gaat per e-mail naar ${t.email})...`}
                                            rows={4}
                                            className={`${inputCls} resize-none`}
                                        />
                                        <div className="flex items-center gap-3">
                                            <button
                                                data-testid={`ticket-reply-send-${t.id}`}
                                                onClick={() => sendReply(t.id)}
                                                disabled={replySending || replyText.trim().length < 2}
                                                className="inline-flex items-center gap-2 rounded-full bg-[#0047FF] hover:bg-[#0036CC] text-white font-bold text-sm px-5 py-2.5 transition-colors disabled:opacity-50"
                                            >
                                                <Send className="w-4 h-4" /> {replySending ? "Versturen..." : "Verstuur per e-mail"}
                                            </button>
                                            <button
                                                data-testid={`ticket-reply-cancel-${t.id}`}
                                                onClick={() => { setReplyFor(null); setReplyText(""); }}
                                                className="text-sm font-bold text-[#0A0A0A]/50 hover:text-[#0A0A0A] transition-colors"
                                            >
                                                Annuleren
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        data-testid={`ticket-reply-open-${t.id}`}
                                        onClick={() => { setReplyFor(t.id); setReplyText(""); }}
                                        className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#0047FF]/30 text-[#0047FF] hover:bg-[#0047FF]/5 font-bold text-sm px-5 py-2.5 transition-colors"
                                    >
                                        <Mail className="w-4 h-4" /> Beantwoord per e-mail
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {tab === "leden" && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-[#0A0A0A]/50">{subscribers.length} {subscribers.length === 1 ? "aanmelding" : "aanmeldingen"}</p>
                            <div className="flex items-center gap-3">
                                <input
                                    data-testid="subscribers-import-file"
                                    ref={importFileRef}
                                    type="file"
                                    accept=".csv,text/csv"
                                    className="hidden"
                                    onChange={importSubscribers}
                                />
                                <button
                                    data-testid="subscribers-import"
                                    onClick={() => importFileRef.current?.click()}
                                    className="inline-flex items-center gap-2 rounded-full border border-[#0047FF]/30 text-[#0047FF] hover:bg-[#0047FF]/5 font-bold text-sm px-5 py-2.5 transition-colors"
                                >
                                    <Upload className="w-4 h-4" /> Upload bestand
                                </button>
                                <button
                                    data-testid="subscribers-export"
                                    onClick={exportSubscribers}
                                    disabled={subscribers.length === 0}
                                    className="inline-flex items-center gap-2 rounded-full bg-[#0047FF] hover:bg-[#0036CC] text-white font-bold text-sm px-5 py-2.5 transition-colors disabled:opacity-50"
                                >
                                    <Download className="w-4 h-4" /> Download als bestand
                                </button>
                            </div>
                        </div>
                        <div data-testid="admin-subscribers" className="rounded-2xl bg-white border border-black/10 overflow-hidden">
                            {subscribers.length === 0 && <p className="p-6 text-[#0A0A0A]/50">Nog geen nieuwsbrief-leden.</p>}
                            {subscribers.map((s) => (
                                <div key={s.id} className="flex items-center justify-between px-6 py-4 border-b border-black/5 last:border-b-0">
                                    <span className="text-sm font-medium">{s.email}</span>
                                    <span className="text-xs text-[#0A0A0A]/40">{s.bron} · {fmtDate(s.created_at)}</span>
                                </div>
                            ))}
                        </div>
                        {backups.length > 0 && (
                            <div data-testid="subscriber-backups" className="rounded-2xl bg-white border border-black/10 p-6">
                                <p className="font-heading font-bold text-base tracking-tight mb-1">Backups</p>
                                <p className="text-xs text-[#0A0A0A]/40 mb-4">Automatisch gemaakt vóór elke upload of herstelactie.</p>
                                <div className="space-y-2">
                                    {backups.map((b) => (
                                        <div key={b.id} className="flex items-center justify-between gap-3 text-sm">
                                            <span className="text-[#0A0A0A]/70">{fmtDate(b.created_at)} · {b.count} {b.count === 1 ? "lid" : "leden"}</span>
                                            <button
                                                data-testid={`backup-restore-${b.id}`}
                                                onClick={() => restoreBackup(b)}
                                                className="text-xs font-bold rounded-full border border-[#0047FF]/30 text-[#0047FF] hover:bg-[#0047FF]/5 px-4 py-1.5 transition-colors"
                                            >
                                                Herstel deze versie
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "berichten" && (
                    <div data-testid="admin-posts" className="space-y-4">
                        <p className="text-sm text-[#0A0A0A]/50">Bevindingen van community-leden. Goedgekeurde berichten staan openbaar op de community-pagina.</p>
                        {posts.length === 0 && <p className="text-[#0A0A0A]/50">Nog geen bevindingen ingestuurd.</p>}
                        {posts.map((p) => (
                            <div key={p.id} className="rounded-2xl bg-white border border-black/10 p-6 flex flex-wrap gap-4 items-start justify-between">
                                <div className="min-w-0">
                                    <p className="font-heading font-bold text-lg tracking-tight">{p.naam}</p>
                                    <p className="text-sm text-[#0A0A0A]/60">{p.email} · {fmtDate(p.created_at)}</p>
                                    <p className="mt-2 text-sm text-[#0A0A0A]/70 bg-[#F9FAFB] rounded-lg p-3">{p.bericht}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <StatusPill status={p.status || "nieuw"} />
                                    {p.status !== "goedgekeurd" && (
                                        <button
                                            data-testid={`post-approve-${p.id}`}
                                            onClick={() => approvePost(p)}
                                            className="text-sm font-bold rounded-full border border-green-600/30 text-green-700 hover:bg-green-50 px-4 py-2 transition-colors"
                                        >
                                            Keur goed
                                        </button>
                                    )}
                                    <button
                                        data-testid={`post-delete-${p.id}`}
                                        onClick={() => deletePost(p)}
                                        className="text-[#0A0A0A]/30 hover:text-red-600 p-2 transition-colors"
                                        aria-label={`Bevinding van ${p.naam} verwijderen`}
                                        title="Verwijderen"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "campagne" && (
                    <div data-testid="admin-campaign" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <form onSubmit={sendCampaign} className="rounded-2xl bg-white border border-black/10 p-6 space-y-4">
                            <p className="font-heading font-bold text-lg tracking-tight">Nieuwsbrief versturen</p>
                            <p className="text-sm text-[#0A0A0A]/50">Gaat naar alle {subscribers.length} nieuwsbrief-leden via MeerjarenplanOnline.nl mail.</p>
                            <input data-testid="campaign-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Onderwerp" className={inputCls} />
                            <textarea data-testid="campaign-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Je bericht aan de community..." rows={8} className={`${inputCls} resize-none`} />
                            <button data-testid="campaign-send" type="submit" disabled={sending || !subject.trim() || !message.trim()} className="inline-flex items-center gap-2 rounded-full bg-[#FF6B00] hover:bg-[#e05e00] text-white font-bold text-sm px-6 py-3 transition-colors disabled:opacity-50">
                                <Send className="w-4 h-4" /> {sending ? "Versturen..." : "Versturen"}
                            </button>
                        </form>
                        <div className="rounded-2xl bg-white border border-black/10 p-6">
                            <p className="font-heading font-bold text-lg tracking-tight mb-4">Verzonden campagnes</p>
                            {campaigns.length === 0 && <p className="text-sm text-[#0A0A0A]/50">Nog niets verzonden.</p>}
                            {campaigns.map((c) => (
                                <div key={c.id} className="border-b border-black/5 last:border-b-0 py-3">
                                    <p className="text-sm font-bold">{c.subject}</p>
                                    <p className="text-xs text-[#0A0A0A]/40">{c.sent} verzonden{c.failed ? `, ${c.failed} mislukt` : ""} · {fmtDate(c.created_at)}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === "bezoeken" && (
                    <div data-testid="admin-visits" className="rounded-2xl bg-white border border-black/10 overflow-hidden">
                        {visits.length === 0 && <p className="p-6 text-[#0A0A0A]/50">Nog geen campagnebezoeken gemeten.</p>}
                        {visits.map((v) => (
                            <div key={v.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-6 py-4 border-b border-black/5 last:border-b-0 text-sm">
                                <span className="font-bold text-[#0047FF]">/{v.slug}</span>
                                <span className="text-[#0A0A0A]/50 text-xs">
                                    {[v.utm_source, v.utm_medium, v.utm_campaign].filter(Boolean).join(" · ") || "geen utm"}
                                </span>
                                <span className="ml-auto text-xs text-[#0A0A0A]/40">{fmtDate(v.created_at)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default Admin;
